import type { SetupPlan } from "../data/migrate";
import { Button } from "./ui/Button";

interface Props {
  plan: SetupPlan;
  busy: boolean;
  onRun: () => void;
}

/**
 * Shown when the sheet has not been prepared yet. It states exactly what will
 * change before anything is written, because this is the one place the app
 * touches rows it did not create.
 */
export function SetupPanel({ plan, busy, onRun }: Props) {
  const lines: string[] = [];
  if (plan.missingHeaders.length) {
    lines.push(`Add the ${plan.missingHeaders.join(", ")} column headers.`);
  }
  if (plan.createProjectsTab) lines.push("Create the Projects tab.");
  if (plan.missingProjectHeaders.length) {
    lines.push(
      `Add the ${plan.missingProjectHeaders.join(" and ")} columns to the Projects tab.`
    );
  }
  if (plan.newProjects.length) {
    lines.push(
      `Add ${plan.newProjects.length} projects: ${plan.newProjects.join(", ")}.`
    );
  }
  if (plan.rowsNeedingId) {
    lines.push(`Give ${plan.rowsNeedingId} task rows a TaskID.`);
  }
  if (plan.rowsNeedingProjectId) {
    lines.push(`Stamp ${plan.rowsNeedingProjectId} rows with their ProjectID.`);
  }
  if (plan.rowsNeedingStatus.length) {
    lines.push(
      `Set ${plan.rowsNeedingStatus.length} rows with no status to Not Started ` +
        `(rows ${plan.rowsNeedingStatus.join(", ")}).`
    );
  }

  return (
    <div className="setup">
      <h1 className="title">Prepare the spreadsheet</h1>
      <p className="setup__lead">
        This runs once. It reads {plan.totalTaskRows} task rows and makes the
        changes below. Nothing is deleted, the Due days formulas are not
        touched, and running it again does nothing.
      </p>
      <ul className="setup__list">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <Button variant="primary" onClick={onRun} disabled={busy}>
        {busy ? "Working…" : "Prepare the sheet"}
      </Button>
    </div>
  );
}
